import { createLogger } from "utils/logging";
import type { Debug } from "@otto-ec/global-resources/debug";
import { retry, policy, tryPromise } from "@otto-ec/assets-core-utils/nano-effect";
import { toggle } from "@otto-ec/global-resources/toggle";
import { kyFetch } from "./ky-fetch.ts";

type Logger = Debug<`benefit.${string}.fetch-retry`>;
type Counter = { value: number };

export interface FetchRetryArgs {
	url: string;
	namespace: string;
	timeout?: number;
	retryOptions: {
		limit: number;
		delay: number;
	};
	fetchOptions?: Exclude<RequestInit, "signal">;
}

function isNotRetryable(statusCode: number): boolean {
	if (statusCode === 408) return false; /*              */
	return 400 <= statusCode && statusCode <= 499;
}

function createTimeout(logger: Logger, url: string, timeout: number | undefined) {
	if (!timeout) {
		return { signal: null, timeoutId: undefined };
	}

	const controller = new AbortController();
	const timeoutId = setTimeout(() => {
		logger.warn(`[fetch] Timeout fetch to ${url}`);
		controller.abort();
	}, timeout);
	return { signal: controller.signal, timeoutId };
}

async function fetchTask(url: string, options: Exclude<RequestInit, "signal"> = {}, logger: Logger, retriesCounter: Counter, timeout: number | undefined): Promise<Response> {
	const { signal, timeoutId } = createTimeout(logger, url, timeout);

	try {
		const response = await fetch(url, { ...options, signal });

		if (response.ok || isNotRetryable(response.status)) {
			return response;
		}

		retriesCounter.value++;
		return Promise.reject(response.status);
	} catch (e) {
		retriesCounter.value++;
		return Promise.reject(e);
	} finally {
		clearTimeout(timeoutId);
	}
}

export async function fetchRetry(args: FetchRetryArgs) {
	if (toggle.get("ftnine_use_ky_fetch", false)) {
		return kyFetch(args);
	}
	const { url, namespace, timeout, retryOptions, fetchOptions } = args;
	const { limit, delay } = retryOptions;
	const logger = createLogger(namespace, "fetch-retry");

	const retriesCounter = { value: 0 };

	const task = () => fetchTask(url, fetchOptions, logger, retriesCounter, timeout);
	const fetchWithRetry = retry(
		tryPromise(task, (err) => err),
		policy({ times: limit, delay }),
	);

	const [error, response] = await fetchWithRetry();

	if (error) {
		throw error;
	}

	return response;
}
